import { Injectable } from '@angular/core';
import { BehaviorSubject,Observable } from 'rxjs';
import { LAW, LEGAL_SITUATION, ESTABLISHMENT_TYPE, INVESTMENT_NATURE, FREE_ZONE_TYPE } from './laws-and-legal-situation-constants';
import { StatusOption } from '../features/workspace/search-form/search-form.component';
import { StorageService, SessionStorageKey } from '../core/services/storage.service';

// 🟢 Define interfaces for type safety
interface Law {
  code: typeof LAW[keyof typeof LAW];
  name: string;
}

interface LegalSituation {
  code: typeof LEGAL_SITUATION[keyof typeof LEGAL_SITUATION];
  name: string;
}

interface establishmentType {
  code: typeof ESTABLISHMENT_TYPE[keyof typeof ESTABLISHMENT_TYPE];
  name: string;
}

interface InvestmentNature {
  code: typeof INVESTMENT_NATURE[keyof typeof INVESTMENT_NATURE];
  name: string;
}

interface FreeZoneType {
  code: typeof FREE_ZONE_TYPE[keyof typeof FREE_ZONE_TYPE];
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class SharedDataService {
  private formData: any = {};

  private selectedLawSource = new BehaviorSubject<any | null>(null);
  selectedLaw$ = this.selectedLawSource.asObservable();

  private selectedLegalSituationSource = new BehaviorSubject<any | null>(null);
  selectedLegalSituation$ = this.selectedLegalSituationSource.asObservable();

  private selectedEstablishmentTypeSource = new BehaviorSubject<any | null>(null);
  selectedEstablishmentType$ = this.selectedEstablishmentTypeSource.asObservable();

  private isLaw72Source = new BehaviorSubject<boolean>(false);
  isLaw72$ = this.isLaw72Source.asObservable();

  private isLaw159Source = new BehaviorSubject<boolean>(false);
  isLaw159$ = this.isLaw159Source.asObservable();

  private selectedInvestmentNatureSource = new BehaviorSubject<any | null>(
    null
  );
  selectedInvestmentNature$ =
    this.selectedInvestmentNatureSource.asObservable();

  private selectedFreeZoneTypeSource = new BehaviorSubject<any | null>(null);
  selectedFreeZoneType$ = this.selectedFreeZoneTypeSource.asObservable();


  // sample request data
  private requestIdSource = new BehaviorSubject<number | null>(null);
  requestId$ = this.requestIdSource.asObservable();

  private requestNumberSource = new BehaviorSubject<string | null>(null);
  requestNumber$ = this.requestNumberSource.asObservable();

  private noteSectionsSubject = new BehaviorSubject<string[]>([]);
  public readonly noteSections$ = this.noteSectionsSubject.asObservable();

  private approveContractChangedSubject = new BehaviorSubject<boolean>(false);
  approveContractChanged$ = this.approveContractChangedSubject.asObservable();

  private requestStatusChangedSubject = new BehaviorSubject<string>('');
  requestStatusChanged$ = this.requestStatusChangedSubject.asObservable();

  private paidFeesChangedSubject = new BehaviorSubject<boolean>(false);
  paidFeesChanged$ = this.paidFeesChangedSubject.asObservable();

  private sendDateSource = new BehaviorSubject<string | Date | null>(null);
  sendDate$ = this.sendDateSource.asObservable();

  setSendDate(sendDate: string | Date | null) {
    this.sendDateSource.next(sendDate);
    if (sendDate) {
      this.storage.setSession(SessionStorageKey.SendDate, typeof sendDate === 'string' ? sendDate : new Date(sendDate).toISOString());
    } else {
      this.storage.removeSession(SessionStorageKey.SendDate);
    }
  }

  getSendDate(): string | null {
    const inMemory = this.sendDateSource.getValue();
    if (inMemory) return typeof inMemory === 'string' ? inMemory : new Date(inMemory).toISOString();
    return this.storage.getSession(SessionStorageKey.SendDate);
  }

  private statusesSource = new BehaviorSubject<StatusOption[]>([]);
  statuses$ = this.statusesSource.asObservable();

  private partnersCountSource = new BehaviorSubject<number>(0);
  partnersCount$ = this.partnersCountSource.asObservable();

  private afterSignContractSource = new BehaviorSubject<boolean>(false);
  afterSignContract$ = this.afterSignContractSource.asObservable();

  private afterAcceptanceDeclerationSignSource = new BehaviorSubject<boolean>(false);
  afterAcceptanceDeclerationSign$ = this.afterAcceptanceDeclerationSignSource.asObservable();

  public isVip$ = new BehaviorSubject<boolean>(false);
  public isPaidFees$ = new BehaviorSubject<boolean>(false);

  setAfterSignContract(value: boolean) {
    this.afterSignContractSource.next(value);
  }

  getAfterSignContract(): boolean {
    return this.afterSignContractSource.value;
  }
  updatePartnersCount(count: number): void {
    this.partnersCountSource.next(count);
  }

  getPartnersCount(): number {
    return this.partnersCountSource.value;
  }

  setStatuses(statuses: StatusOption[]) {
    this.statusesSource.next(statuses);
  }

  setApproveContract(value: boolean) {
    this.approveContractChangedSubject.next(value);
  }
  setRequestStatus(value: string) {
    if(this.requestStatusChangedSubject.getValue() === value) return
    this.requestStatusChangedSubject.next(value);
  }
  getRequestStatus(): string | null {
    return this.requestStatusChangedSubject.getValue();
  }
  setPaidFees(value: boolean) {
    if(this.paidFeesChangedSubject.getValue() === value) return
    this.paidFeesChangedSubject.next(value);
  }
  getPaidFees(): boolean {
    return this.paidFeesChangedSubject.getValue();
  }
  
  // New: Method to update note sections (called from wizard after fetch)
  updateNoteSections(sections: string[]): void {
    this.noteSectionsSubject.next(sections);
  }
  getNoteSections(): string[] {
    return this.noteSectionsSubject.value;
  }
  setRequestNumber(requestNumber: string) {
    this.requestNumberSource.next(requestNumber);
    this.storage.setSession(SessionStorageKey.RequestNumber, requestNumber);
  }

  getRequestNumber(): string | null {
    return this.storage.getSession(SessionStorageKey.RequestNumber);
  }



  setRequestId(id: number) {
    this.requestIdSource.next(id);
    this.storage.setSession(SessionStorageKey.RequestId, id.toString());
  }

  getRequestId(): number | null {
    const storedId = this.storage.getSession(SessionStorageKey.RequestId);
    return storedId ? Number(storedId) : null;
  }

  setStepData(stepKey: string, data: any) {
    this.formData[stepKey] = data;
  }

  getStepData(stepKey: string) {
    return this.formData[stepKey] || {};
  }

  getAllData() {
    return this.formData;
  }

  getIsVip(): boolean {
    return this.isVip$.value;
  }

  setIsVip(value: boolean) {
    this.isVip$.next(value);
  }


  getIsPaidFees(): boolean {
    return this.isPaidFees$.value;
  }

  setIsPaidFees(value: boolean) {
    this.isPaidFees$.next(value);
  }

  constructor(private storage: StorageService) {
    const law = this.storage.getSessionJson(SessionStorageKey.SelectedLaw);
    if (law) {
      this.selectedLawSource.next(law);
    }

    const situation = this.storage.getSessionJson(SessionStorageKey.SelectedLegalSituation);
    if (situation) {
      this.selectedLegalSituationSource.next(situation);
    }

    const type = this.storage.getSessionJson(SessionStorageKey.SelectedEstablishmentType);
    if (type) {
      this.selectedEstablishmentTypeSource.next(type);
    }

    const nature = this.storage.getSessionJson(SessionStorageKey.SelectedInvestmentNature);
    if (nature) {
      this.setSelectedInvestmentNature(nature);
    }
  }

  setSelectedLaw(law: any) {
    this.selectedLawSource.next(law);
    this.storage.setSessionJson(SessionStorageKey.SelectedLaw, law);

    this.isLaw72Source.next(false);
    this.isLaw159Source.next(false);

    if (law?.code === LAW.Law72) {
      this.isLaw72Source.next(true);
    } else if (law?.code === LAW.Law159) {
      this.isLaw159Source.next(true);
    }
  }

  setSelectedLegalSituation(situation: any) {
    this.selectedLegalSituationSource.next(situation);
    this.storage.setSessionJson(SessionStorageKey.SelectedLegalSituation, situation);
  }

  setSelectedEstablishmentType(type: any) {
    this.selectedEstablishmentTypeSource.next(type);
    this.storage.setSessionJson(SessionStorageKey.SelectedEstablishmentType, type);
  }

  // 🟢 Method to set selectedInvestmentNature
  setSelectedInvestmentNature(nature: any) {
    this.selectedInvestmentNatureSource.next(nature);
    this.storage.setSessionJson(SessionStorageKey.SelectedInvestmentNature, nature);
  }

  // 🟢 NEW: Method to set selectedFreeZoneType
  setSelectedFreeZoneType(freeZoneType: any) {
    this.selectedFreeZoneTypeSource.next(freeZoneType);
    this.storage.setSessionJson(SessionStorageKey.SelectedFreeZoneType, freeZoneType);
  }
  getFoundationState() {
    const law = this.selectedLawSource.value;
    const legalSituation = this.selectedLegalSituationSource.value;
    const establishmentType = this.selectedEstablishmentTypeSource.value;
    const investmentNature = this.selectedInvestmentNatureSource.value;
    const freeZoneType = this.selectedFreeZoneTypeSource.value;

    return {
      lawType: law?.id || null,
      legalForm: legalSituation?.id || null,
      establishmentTypeId: establishmentType?.id || null,
      investmentNatureId: investmentNature?.id || null,
      freeZoneTypeId: freeZoneType?.id || null,
      isLaw72: this.isLaw72Source.value,
      isLaw159: this.isLaw159Source.value,
      isFreeZoneNatureSelected: investmentNature?.code === 'FZ',
      acceptTerms: true, // أو خليها تيجي من الفورم/checkbox عندك
    };
  }

arabicNumberToWords(
  num: any,
  currencyCode: 'EGP'| 'USD'| 'EUR'| 'GBP'| 'SAR'| 'AED'| 'KWD'| 'BHD'| 'QAR'| 'OMR'| 'JOD'| 'CHF'| 'JPY'| 'AUD'| 'CAD'| 'CNY'| 'RUB'| 'SEK'| 'NOK'| 'DKK'| 'THB'| 'XOF' = 'EGP',
): string {
  if (num == null || isNaN(num)) return 'صفر';

  num = Number(num);
  if (num < 0) return 'صفر';

  const integerPart = Math.floor(num);
  const fractionPart = Math.round((num - integerPart) * 100);

  const noFractionCurrencies = ['JPY'];
  const hasFractionalUnit = !noFractionCurrencies.includes(currencyCode);

    // تعريف العملات
    const currencies: any = {
      EGP: buildCurrency('جنيه مصري','جنيهان مصريان','جنيهات مصرية','قرش','قرشان','قروش'),

      USD: buildCurrency('دولار','دولاران','دولارات','سنت','سنتان','سنتات'),

      EUR: buildCurrency('يورو', 'يورو', 'يورو', 'سنت', 'سنتان', 'سنتات'),

      GBP: buildCurrency('جنيه استرليني','جنيهان استرلينيان','جنيهات استرلينية','بنس','بنسان','بنسات'),

      SAR: buildCurrency('ريال سعودي','ريالان سعوديان','ريالات سعودية','هللة','هللتان','هللات'),

      AED: buildCurrency('درهم إماراتي','درهمان إماراتيان','دراهم إماراتية','فلس','فلسان','فلوس'),

      KWD: buildCurrency('دينار كويتي','ديناران كويتيان','دنانير كويتية','فلس','فلسان','فلوس'),

      BHD: buildCurrency('دينار بحريني','ديناران بحرينيان','دنانير بحرينية','فلس','فلسان','فلوس'),

      QAR: buildCurrency('ريال قطري','ريالان قطريان','ريالات قطرية','فلس','فلسان','فلوس'),

      OMR: buildCurrency('ريال عماني','ريالان عمانيان','ريالات عمانية','فلس','فلسان','فلوس'),

      JOD: buildCurrency('دينار أردني','ديناران أردنيان','دنانير أردنية','فلس','فلسان','فلوس'),

      CHF: buildCurrency('فرنك سويسري','فرنكان سويسريان','فرنكات سويسرية','سنتيم','سنتيمان','سنتيمات'),

      JPY: buildCurrency('100 ين ياباني'),

      AUD: buildCurrency('دولار أسترالي','دولاران أستراليان','دولارات أسترالية','سنت','سنتان','سنتات'),

      CAD: buildCurrency('دولار كندي','دولاران كنديان','دولارات كندية','سنت','سنتان','سنتات'),

      CNY: buildCurrency('يوان صيني','يوانان صينيان','يوانات صينية','فن','فنان','فنات'),

      RUB: buildCurrency('روبل روسي','روبلان روسيان','روبلات روسية','كوبيك','كوبيكان','كوبيكات'),

      SEK: buildCurrency('كرونا سويدية','كرونتان سويديتان','كرونات سويدية','أوره','أورتان','أورات'),

      NOK: buildCurrency('كرونا نرويجية','كرونتان نرويجيتان','كرونات نرويجية','أوره','أورتان','أورات'),

      DKK: buildCurrency('كرونا دنماركية','كرونتان دنماركيتان','كرونات دنماركية','أوره','أورتان','أورات'),

      THB: buildCurrency('بات تايلاندي','باتان تايلانديان','باتات تايلاندية','ساتانغ','ساتانغان','ساتانغات'),

      XOF: buildCurrency('فرنك غرب أفريقي','فرنكان غرب أفريقيان','فرنكات غرب أفريقية','سنتيم','سنتيمان','سنتيمات'),
    };

  const currency = currencies[currencyCode];

  const ones = ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة'];
  const tens = ['', 'عشرة', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
  const teens = [
    'عشرة', 'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر',
    'خمسة عشر', 'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر',
  ];
  const hundreds = [
    '', 'مائة', 'مائتان', 'ثلاثمائة', 'أربعمائة',
    'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة',
  ];

  const convertUnder1000 = (n: number): string => {
    let result = '';

    if (n >= 100) {
      result += hundreds[Math.floor(n / 100)];
      n %= 100;
      if (n > 0) result += ' و';
    }

    if (n >= 10 && n < 20) {
      return result + teens[n - 10];
    }

    if (n >= 20) {
      const onesDigit = n % 10;
      const tensDigit = Math.floor(n / 10);

      if (onesDigit > 0) {
        result += ones[onesDigit] + ' و';
      }
      result += tens[tensDigit];
      return result;
    }

    if (n > 0) result += ones[n];
    return result;
  };

  const convertNumber = (n: number): string => {
    if (n === 0) return 'صفر';

    let result = '';

    const billions = Math.floor(n / 1_000_000_000);
    if (billions > 0) {
      result += convertUnder1000(billions) + ' مليار';
      n %= 1_000_000_000;
      if (n > 0) result += ' و';
    }

    const millions = Math.floor(n / 1_000_000);
    if (millions > 0) {
      result += convertUnder1000(millions) + ' مليون';
      n %= 1_000_000;
      if (n > 0) result += ' و';
    }

    const thousands = Math.floor(n / 1000);
    if (thousands > 0) {
      result += (thousands === 1 ? 'ألف' : convertUnder1000(thousands) + ' آلاف');
      n %= 1000;
      if (n > 0) result += ' و';
    }

    if (n > 0) result += convertUnder1000(n);
    return result;
  };

  // صياغة العملة
  const formatCurrency = (value: number) => {
    // if (value === 1) return currency.one;
    // if (value === 2) return currency.two;
    return currency.one;
  };

  const formatFraction = (value: number) => {
    // if (value === 1) return currency.fractionOne;
    // if (value === 2) return currency.fractionTwo;
    return currency.fractionOne;
  };

  let words = integerPart > 0
    ? `${convertNumber(integerPart)} ${formatCurrency(integerPart)}`
    : `صفر ${currency.one}`;

  if (hasFractionalUnit && fractionPart > 0) {
    words += ` و ${convertNumber(fractionPart)} ${formatFraction(fractionPart)}`;
  }

  return words.trim();
}
  getAfterAcceptanceDeclerationSignValue(): boolean {
    return this.afterAcceptanceDeclerationSignSource.value;
  }

  setAfterAcceptanceDeclerationSignValue(value: boolean): void {
    this.afterAcceptanceDeclerationSignSource.next(value);
  }
}
const buildCurrency = (
  one: string,
  two?: string,
  many?: string,
  fractionOne?: string,
  fractionTwo?: string,
  fractionMany?: string
) => ({
  one,
  two: two || one,
  many: many || one,
  fractionOne: fractionOne || one,
  fractionTwo: fractionTwo || fractionOne || one,
  fractionMany: fractionMany || fractionOne || one,
});
